import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t } from "@/i18n/translations";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = document.querySelectorAll('section[id]');
      sections.forEach((section) => {
        const el = section as HTMLElement;
        const top = el.offsetTop - 100;
        const id = section.getAttribute('id') || '';
        if (window.scrollY >= top && window.scrollY < top + el.offsetHeight) {
          setActiveSection(id);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t(translations.nav.home, language), href: "#home" },
    { label: t(translations.nav.about, language), href: "#about" },
    { label: t(translations.nav.services, language), href: "#services" },
    { label: t(translations.nav.projects, language), href: "#projects" },
    { label: t(translations.nav.partners, language), href: "#partners" },
    { label: t(translations.nav.contact, language), href: "#contact" },
  ];

  return (
    <header className={`fixed w-full top-[52px] z-50 transition-all duration-300 ${scrolled ? 'bg-[hsl(var(--brand-navy))]/95 backdrop-blur-lg shadow-lg' : 'bg-[hsl(var(--brand-navy))]/80 backdrop-blur-sm'}`}>
      <nav className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center w-full lg:justify-center">
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <a
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  activeSection === item.href.substring(1)
                    ? 'text-white bg-white/20'
                    : 'text-white/90 hover:text-white hover:bg-white/15'
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <button className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-40 lg:hidden"
                onClick={() => setIsMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden absolute top-full left-0 right-0 bg-[hsl(var(--brand-navy))] border-t border-white/10 shadow-xl z-50 py-2 px-4"
              >
                {navItems.map(item => (
                  <a
                    key={item.href}
                    href={item.href}
                    className={`block px-4 py-3 rounded-lg text-[15px] font-medium transition-all ${
                      activeSection === item.href.substring(1)
                        ? 'text-white bg-white/15'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
